import { useEffect, useState } from "react";
import { Checkbox, Modal, Switch, TransparentHBLoadingSpinnerOnly } from "~components";
import { Css } from "~generated/css";
import { safeEntries, safeKeys } from "~utils";
import { PrintSummaryReports, PrintSummaryReportSlugs } from "./SummaryPrint.page";
import { useSummaryPrintConfigurationQuery } from "./SummaryPrint.page.generated";

export type PDFPrintModalProps = {
  onClose: () => void;
  onContinue: (exports: string[], includePricing: boolean) => void;
  checkoutConfigId: string;
  hasAduSupport: boolean;
};

export function PDFPrintModal(props: PDFPrintModalProps) {
  const { onClose, onContinue, checkoutConfigId, hasAduSupport } = props;

  // default state for all of these is falsey so we don't need keys or values here
  const [checkValues, setCheckValues] = useState<{ [key in PrintSummaryReportSlugs]?: boolean }>({ summary: true });
  const [includePricing, setIncludePricing] = useState(false);

  const isInteriorSelected = checkValues.interior ?? false;

  // Query to check if InsideMaps room renderings are ready (only when Interior is selected)
  const query = useSummaryPrintConfigurationQuery({
    variables: { checkoutConfigId },
    skip: !isInteriorSelected,
  });

  const hasInteriorImages = (query.data?.checkoutConfig?.insideMapsRoomRenderings ?? []).nonEmpty;
  const shouldPoll = isInteriorSelected && !hasInteriorImages;

  /** InsideMaps images may be processing on the backend. Detect if that's happening, and start polling until images are ready. */
  useEffect(() => {
    shouldPoll ? query.startPolling(2_500) : query.stopPolling();
  }, [query, shouldPoll]);

  return (
    <Modal
      size={{ width: "696px", height: "498" }}
      title={
        <div css={Css.df.jcfs.gap2.pb4.$}>
          <span css={Css.t24.$}>Select what to include in PDF export</span>
        </div>
      }
      description={
        <div css={Css.t18.fw300.wPx(568).$}>
          {safeEntries(PrintSummaryReports)
            .filter(([key]) => key !== "adu" || hasAduSupport)
            .map(([key, { label }]) => {
              return (
                <div css={Css.df.mb2.$} key={key}>
                  <Checkbox
                    selected={checkValues[key] ?? false}
                    onClick={() => setCheckValues({ ...checkValues, [key]: !checkValues[key] })}
                  />
                  <p css={Css.t14.gray700.ml2.lhPx(22).$}>{label}</p>
                </div>
              );
            })}
          <div css={Css.df.mb2.$}>
            <Switch value={includePricing} onClick={() => setIncludePricing(!includePricing)} />
            <p css={Css.t14.gray700.ml2.$}>Include pricing</p>
          </div>
        </div>
      }
      actionBtns={[
        { variant: "link", children: "Cancel", onClick: onClose },
        {
          children: (
            <div css={Css.df.aic.gap1.$}>
              {shouldPoll && (
                <span css={Css.df.aic.jcc.wPx(20).hPx(20).$}>
                  <TransparentHBLoadingSpinnerOnly />
                </span>
              )}
              <span>{shouldPoll ? "Rendering" : "Export"}</span>
            </div>
          ),
          disabled: shouldPoll
            ? "Preparing interior images for export..."
            : Object.values(checkValues).every((value) => !value),
          onClick: () => {
            const exports = safeKeys(checkValues).reduce((acc: string[], current) => {
              if (checkValues[current]) {
                acc.push(current);
              }
              return acc;
            }, []);

            onContinue(exports, includePricing);
            onClose();
          },
        },
      ]}
      isOpen
      close={onClose}
    ></Modal>
  );
}
